// 레지스트리 스킨 전역 캐싱 시스템

import { searchSkins } from '../services/skinApi';

// 3단계 캐싱 시스템
class RegistrySkinsCache {
  constructor() {
    // 1단계: 메모리 캐시 (가장 빠름)
    this.memoryCache = new Map();
    
    // 캐시 설정
    this.CACHE_DURATION = 5 * 60 * 1000; // 5분
    this.SESSION_KEY = 'registryLayouts';
    
    // Pre-loading 상태
    this.isPreloading = false;
    this.preloadPromise = null;
  }

  // 메모리 캐시 확인
  getFromMemory(key = 'registry_skins') {
    const cached = this.memoryCache.get(key);
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_DURATION) {
      this.memoryCache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  // sessionStorage 캐시 확인
  getFromSessionStorage() {
    try {
      const stored = sessionStorage.getItem(this.SESSION_KEY);
      if (!stored) return null;
      
      const { data, timestamp } = JSON.parse(stored);
      const now = Date.now();
      
      if (now - timestamp > this.CACHE_DURATION) {
        sessionStorage.removeItem(this.SESSION_KEY);
        return null;
      }
      

      // 메모리 캐시에도 저장
      this.setInMemory('registry_skins', data);
      
      return data;
    } catch (error) {
      console.warn('sessionStorage 읽기 실패:', error);
      return null;
    }
  }

  // 메모리 캐시에 저장
  setInMemory(key, data) {
    this.memoryCache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // sessionStorage에 저장
  setInSessionStorage(data) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('sessionStorage 저장 실패:', error);
    }
  }

  // API에서 데이터 로드
  async loadFromAPI() {
    try {
      const allSkins = await searchSkins('LAYOUT', '');
      const registrySkins = allSkins.filter(skin => skin.hasPermission);
      

      // 스킨 데이터 유효성 검증
      const validSkins = [];
      const invalidSkins = [];
      
      registrySkins.forEach(skin => {
        const isValid = this.validateSkinData(skin);
        if (isValid) {
          validSkins.push(skin);
        } else {
          invalidSkins.push(skin);
        }
      });
      
      if (invalidSkins.length > 0) {
        console.warn(`⚠️ ${invalidSkins.length}개의 불완전한 스킨 데이터가 발견되어 캐시에서 제외됩니다:`, 
          invalidSkins.map(skin => ({ id: skin.id, name: skin.name })));
      }
      
      // 레이아웃 형식으로 변환 (유효한 스킨만)
      const formattedLayouts = validSkins.map(skin => ({
        id: `registry_${skin.id}`,
        name: skin.name,
        description: skin.description,
        category: '레지스트리',
        thumbnail: skin.thumbnail,
        skinData: skin,
        options: [],
        isOwned: true
      }));


      // 유효한 데이터만 캐시에 저장
      this.setInMemory('registry_skins', formattedLayouts);
      this.setInSessionStorage(formattedLayouts);
      
      return formattedLayouts;
    } catch (error) {
      console.error('API에서 레지스트리 스킨 로드 실패:', error);
      // 에러 발생 시 캐시 무효화
      this.clearCache();
      throw error;
    }
  }
  
  // 스킨 데이터 유효성 검증
  validateSkinData(skin) {

    const validationChecks = {
      hasId: !!skin.id,
      hasName: !!skin.name,
      hasPermission: skin.hasPermission === true,
      hasLatestVersion: !!skin.latestVersion,
      hasJsFileUrl: !!(skin.latestVersion && skin.latestVersion.jsFileUrl),
      hasCssFileUrl: !!(skin.latestVersion && skin.latestVersion.cssFileUrl)
    };
    
    const isValid = Object.values(validationChecks).every(check => check);
    
    if (!isValid) {
      console.warn(`❌ 스킨 ${skin.name}(ID: ${skin.id}) 유효성 검증 실패:`, validationChecks);
      console.warn('상세 정보:', {
        skinId: skin.id,
        skinName: skin.name,
        hasPermission: skin.hasPermission,
        latestVersion: skin.latestVersion
      });
    } else {
    }
    
    return isValid;
  }

  // 통합 캐시 로드 (3단계 폴백)
  async getRegistrySkins() {
    // 1단계: 메모리 캐시 확인 (0ms)
    const memoryData = this.getFromMemory();
    if (memoryData) {
      // 캐시된 데이터 유효성 검증
      const validCachedData = this.validateCachedData(memoryData);
      if (validCachedData) {
        return validCachedData;
      } else {
        console.warn('⚠️ 메모리 캐시에 불완전한 데이터가 발견되어 삭제합니다');
        this.memoryCache.delete('registry_skins');
      }
    }

    // 2단계: sessionStorage 확인 (1-2ms)
    const sessionData = this.getFromSessionStorage();
    if (sessionData) {
      // 캐시된 데이터 유효성 검증
      const validCachedData = this.validateCachedData(sessionData);
      if (validCachedData) {
        return validCachedData;
      } else {
        console.warn('⚠️ sessionStorage에 불완전한 데이터가 발견되어 삭제합니다');
        sessionStorage.removeItem(this.SESSION_KEY);
      }
    }

    // 3단계: API 호출 (최후 수단)
    return await this.loadFromAPI();
  }
  
  // 캐시된 데이터 유효성 검증
  validateCachedData(cachedLayouts) {
    if (!Array.isArray(cachedLayouts)) {
      console.warn('캐시된 데이터가 배열이 아닙니다');
      return null;
    }
    
    const validLayouts = [];
    const invalidLayouts = [];
    
    cachedLayouts.forEach(layout => {
      if (layout.skinData && this.validateSkinData(layout.skinData)) {
        validLayouts.push(layout);
      } else {
        invalidLayouts.push(layout);
      }
    });
    
    if (invalidLayouts.length > 0) {
      console.warn(`⚠️ 캐시에서 ${invalidLayouts.length}개의 불완전한 레이아웃 데이터 발견:`, 
        invalidLayouts.map(layout => ({ id: layout.id, name: layout.name })));
    }
    
    // 유효한 데이터만 반환 (일부라도 유효하면 사용)
    return validLayouts.length > 0 ? validLayouts : null;
  }

  // 백그라운드 pre-loading (사용자가 모르게)
  async preloadSilently() {
    if (this.isPreloading) {
      return this.preloadPromise;
    }

    this.isPreloading = true;

    this.preloadPromise = this.loadFromAPI()
      .then((data) => {
        return data;
      })
      .catch((error) => {
        console.warn('⚠️ 백그라운드 pre-loading 실패 (사용자에게 영향 없음):', error);
        return null;
      })
      .finally(() => {
        this.isPreloading = false;
        this.preloadPromise = null;
      });

    return this.preloadPromise;
  }

  // 특정 스킨 ID로 스킨 데이터 찾기
  async getSkinDataById(skinId) {
    const layouts = await this.getRegistrySkins();
    
    // 타입 안전한 비교 (문자열 vs 숫자)
    const targetId = skinId;
    const layout = layouts.find(layout => {
      const layoutSkinId = layout.skinData?.id;
      // 문자열과 숫자 모두 비교 (loose equality)
      return layoutSkinId == targetId || String(layoutSkinId) === String(targetId);
    });
    

    return layout?.skinData || null;
  }

  // 캐시 초기화
  clearCache() {
    this.memoryCache.clear();
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  // 캐시 상태 확인
  getCacheStatus() {
    const memoryCount = this.memoryCache.size;
    const hasSessionStorage = !!sessionStorage.getItem(this.SESSION_KEY);
    
    return {
      memory: memoryCount > 0,
      sessionStorage: hasSessionStorage,
      isPreloading: this.isPreloading
    };
  }
}

// 전역 캐시 인스턴스
const registrySkinsCache = new RegistrySkinsCache();

export default registrySkinsCache;

// 편의 함수들 export
export const getRegistrySkins = () => registrySkinsCache.getRegistrySkins();
export const preloadRegistrySkins = () => registrySkinsCache.preloadSilently();
export const getSkinDataById = (skinId) => registrySkinsCache.getSkinDataById(skinId);
export const clearRegistryCache = () => registrySkinsCache.clearCache();